/**
 * CatchTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function CatchTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [cd, setCd] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [reveal, setReveal] = useState(false);
  // While the catch animation plays we keep the rarity card hidden so
  // the "what did I catch?" reveal happens at the climax of the video.
  const [playingVideo, setPlayingVideo] = useState(false);
  const videoRef = useRef(null);
  const CATCH_VIDEO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/animations/catch.mp4`;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    // Admins lowering a cooldown duration mid-session should immediately
    // free the user's countdown — clear our local timestamp and let the
    // next action either succeed or return a fresh 429 with the new
    // (clamped) expires_at.
    const onCdUpdate = () => setCd(null);
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => {
      clearInterval(t);
      window.removeEventListener("chillets:cooldown-update", onCdUpdate);
    };
  }, []);

  const tryCatch = async () => {
    setBusy(true);
    setReveal(false);
    setLast(null);
    // Start the throw animation first — the user sees the girl wind up
    // and toss the sphere while the API call is in flight. We delay the
    // reveal until the video naturally ends (or 4s as a safety fallback)
    // so the reward feels earned, not pre-spoiled.
    setPlayingVideo(true);
    try {
      const { data } = await api.post("/palgame/catch");
      setLast(data);
      setCd(new Date(data.cooldown_expires).getTime());
      // `reveal` and the payout flash are triggered by the video's
      // onEnded handler below, so the rarity card + Chillet Coins drop
      // hit at the same beat as the sphere landing.
      refreshProfile();
    } catch (e) {
      // On error abort the video too — we're not going to show anything.
      setPlayingVideo(false);
      const detail = e?.response?.data?.detail;
      if (detail?.expires_at) setCd(new Date(detail.expires_at).getTime());
      toast.error(detail?.message || apiError(e));
    } finally { setBusy(false); }
  };

  // Trigger the rarity + coin reveal at the climax of the video. We
  // queue this from both the natural `ended` event and a safety
  // timeout so a broken codec / autoplay block can't strand the UI.
  const finishCatchReveal = () => {
    setPlayingVideo(false);
    if (last) {
      setReveal(true);
      if (last.reward > 0) {
        firePalgamePayout(last.reward, `CAUGHT ${(last.rarity || "").toUpperCase()}`);
      }
    }
  };

  useEffect(() => {
    if (!playingVideo) return;
    // Safety net: if the video element refuses to play (Safari autoplay
    // policy, slow asset fetch, etc.) reveal after 4 seconds anyway.
    const t = setTimeout(() => {
      if (last) finishCatchReveal();
    }, 4500);
    return () => clearTimeout(t);
  }, [playingVideo, last]); // eslint-disable-line react-hooks/exhaustive-deps

  const remain = cd ? Math.max(0, cd - now) : 0;
  const m = Math.floor(remain / 60000);
  const s = Math.floor((remain % 60000) / 1000);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass-card p-8 text-center fade-up">
        <ChilletSkull size={64} className="text-[var(--teal)] mx-auto mb-4 wisp" style={{ filter: "drop-shadow(0 0 16px var(--teal-glow))" }} />
        <h3 className="text-2xl mb-2">Throw a Pal Sphere</h3>
        <p className="text-sm text-[var(--muted)] mb-6">90-second cooldown. Random rarity. Already-owned Pals are skipped.</p>
        <button onClick={tryCatch} disabled={busy || remain > 0} className="btn btn-lg" data-testid="catch-button">
          {remain > 0 ? `Cooldown ${m}:${String(s).padStart(2, "0")}` : busy ? "Throwing…" : "Catch a Pal!"}
        </button>
      </div>

      <div className="glass-card p-8 flex items-center justify-center min-h-[280px] fade-up relative overflow-hidden" style={{ animationDelay: "0.1s" }}>
        {/* Rarity flash — synced to the moment the video ends and the
            card reveals. Pulses once in the rarity colour and fades.
            common→emerald, rare→sky, epic→fuchsia, legendary→amber. */}
        {reveal && last?.rarity && (
          <motion.div
            key={`flash-${last.caught}`}
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className={`pointer-events-none absolute inset-0 rounded-lg rarity-flash rarity-flash-${last.rarity.toLowerCase()}`}
            data-testid={`catch-flash-${last.rarity.toLowerCase()}`}
          />
        )}
        <AnimatePresence mode="wait">
          {playingVideo ? (
            // Throw animation — plays while we wait for the catch to
            // resolve. `playsInline` is essential for iOS Safari.
            <motion.video
              key="catch-video"
              ref={videoRef}
              src={CATCH_VIDEO_URL}
              autoPlay
              muted
              playsInline
              onEnded={finishCatchReveal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="absolute inset-0 w-full h-full object-cover rounded-lg"
              data-testid="catch-video"
            />
          ) : last && reveal ? (
            <motion.div
              key={last.caught}
              initial={{ scale: 0.5, opacity: 0, rotateY: 90 }}
              animate={{ scale: 1, opacity: 1, rotateY: 0 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.6, type: "spring" }}
              className={`text-center p-6 border-2 rounded-lg w-full max-w-xs rarity-${last.rarity.toLowerCase()} ${last.is_lucky ? "ring-2 ring-yellow-300/70 shadow-[0_0_40px_rgba(255,196,90,0.55)]" : ""}`}
              data-testid="catch-result"
            >
              {last.is_lucky && (
                <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/60 border border-yellow-400/40 text-[10px] uppercase tracking-[0.25em] text-yellow-200"
                     data-testid="catch-lucky-banner">
                  ✨ Lucky!
                </div>
              )}
              <div className="text-xs uppercase tracking-[0.2em] mb-1 opacity-70">Caught</div>
              <PalPortrait name={last.caught} size="w-32 h-32 mx-auto mb-3" />
              <div className="text-3xl mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{last.caught}</div>
              <RarityBadge rarity={last.rarity} />
              <div className="mt-3 flex flex-wrap justify-center gap-1">
                {(last.elements || []).map((e) => <ElementChip key={e} element={e} size={14} />)}
              </div>
              {Array.isArray(last.passives) && last.passives.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-center gap-1 max-w-full"
                     data-testid="catch-passives">
                  {last.passives.map((p) => (
                    <span key={p}
                          className="text-[10px] px-2 py-0.5 rounded border border-white/15 bg-white/5 text-white/80 uppercase tracking-wider">
                      {p}
                    </span>
                  ))}
                </div>
              )}
              {last.talents && (
                <div className="mt-3 text-[10px] text-[var(--muted)] flex justify-center gap-3 font-mono">
                  <span className="text-emerald-300/80">HP {last.talents.hp}</span>
                  <span className="text-rose-300/80">ATK {last.talents.atk}</span>
                  <span className="text-sky-300/80">DEF {last.talents.def}</span>
                </div>
              )}
              <div className="mt-3 text-base"><Currency value={last.reward} /> earned</div>
            </motion.div>
          ) : (
            // Pre-throw state: show the first frame of the catch video
            // as a tease so the card isn't empty before the first
            // click. `preload="metadata"` + #t=0.1 forces Safari and
            // friends to render a frame instead of a black box.
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 rounded-lg overflow-hidden"
              data-testid="catch-preview"
            >
              <video
                src={`${CATCH_VIDEO_URL}#t=0.1`}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                <p className="text-[var(--bone)] text-sm tracking-wider uppercase">Throw a sphere to begin…</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

